import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import MaterialTable from "material-table";
import Grid from "@material-ui/core/Grid";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import CardFooter from "components/Card/CardFooter.js";
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import Button from "@material-ui/core/Button";
import Icon from "@material-ui/core/Icon";
import SnackBarWiiba from "components/Snackbar/SnackBarWiiba";
import useMessage from "../../hooks/useMessage";
import Api from "../../functions/Api";
import { ValidationResponse } from "../../functions/validation";

const useStyles = makeStyles(theme => ({
  root: { width: "100%" },
  cardCategory: { color: "#999", margin: "0", fontSize: "14px", marginTop: "0", paddingTop: "10px", marginBottom: "0" },
  cardTitle: { color: "#3C4858", marginTop: "0px", minHeight: "auto", fontWeight: "300", marginBottom: "3px", textDecoration: "none",
    "& small": { color: "#777", fontWeight: "400", lineHeight: "1" }
  },
  stats: { color: "#999", display: "inline-flex", fontSize: "12px", lineHeight: "22px",
    "& svg": { top: "4px", width: "16px", height: "16px", position: "relative", marginRight: "3px", marginLeft: "3px" }
  },
  successText: { color: "#4caf50" },
  dangerText: { color: "#f44336" },
}));

const columns = [
  { title: "Fecha", field: "fecha", type: "datetime" },
  { title: "Concepto", field: "concepto", type: "string" },
  { title: "Tipo", field: "tipoDescripcion", type: "string",
    render: rowData => (
      <span style={{ color: rowData.tipo === "E" ? "#4caf50" : "#f44336", fontWeight: "bold" }}>
        {rowData.tipoDescripcion}
      </span>
    )
  },
  { title: "Monto", field: "monto", type: "currency", currencySetting: { locale: "es-MX", currencyCode: "MXN" } },
  { title: "Referencia", field: "referencia", type: "string" },
  { title: "Método Pago", field: "metodoPago", type: "string" },
];

export default function MovimientosCaja() {
  const classes = useStyles();
  const [ejecutaapi] = Api();
  const [movimientos, setMovimientos] = useState([]);
  const [infoCaja, setInfoCaja] = useState(null);
  const [totales, setTotales] = useState({ ingresos: 0, egresos: 0, saldo: 0 });

  const { opensnack, typesnackbar, message, handleMuestraSnack, handleClosesSnack } = useMessage();

  const usuario = localStorage.getItem("user");
  const usuarioObj = usuario ? JSON.parse(usuario) : {};

  const cargarMovimientos = () => {
    const body = JSON.stringify({ idCajero: usuarioObj.userId });
    ejecutaapi("ObtieneMovimientosCajaAbierta", body).then(data => {
      let { result, error } = ValidationResponse(data);
      if (error !== "") {
        handleMuestraSnack(error, "error");
        return;
      }

      // El SP retorna 2 result sets: [0] = info caja, [1] = movimientos
      if (result[0] && result[0].error === 1) {
        handleMuestraSnack(result[0].msg, "warning");
        setInfoCaja(null);
        setMovimientos([]);
        return;
      }

      if (result[0]) {
        setInfoCaja(result[0]);
      }

      const movs = result[1] || [];
      setMovimientos(Array.isArray(movs) ? movs : []);

      // Calcular totales
      let ingresos = 0, egresos = 0;
      (Array.isArray(movs) ? movs : []).forEach(m => {
        if (m.tipo === "E") ingresos += parseFloat(m.monto || 0);
        else if (m.tipo === "S") egresos += parseFloat(m.monto || 0);
      });

      const montoInicial = result[0] ? parseFloat(result[0].montoInicial || 0) : 0;
      setTotales({
        ingresos: ingresos.toFixed(2),
        egresos: egresos.toFixed(2),
        saldo: (montoInicial + ingresos - egresos).toFixed(2)
      });

      handleMuestraSnack("Movimientos actualizados", "success");
    }).catch(() => {
      handleMuestraSnack("Error al cargar movimientos", "error");
    });
  };

  useEffect(() => {
    cargarMovimientos();
  }, []);

  return (
    <div className={classes.root}>
      <SnackBarWiiba opensnack={opensnack} tipoSnackBar={typesnackbar} mensaje={message} handleClosesSnack={handleClosesSnack} />

      <GridContainer>
        <GridItem xs={12} sm={4}>
          <Card>
            <CardHeader color="success" stats icon>
              <CardIcon color="success"><Icon>arrow_downward</Icon></CardIcon>
              <p className={classes.cardCategory}>Total Ingresos</p>
              <h3 className={classes.cardTitle}>${totales.ingresos}</h3>
            </CardHeader>
            <CardFooter stats>
              <div className={classes.stats}>Entradas de efectivo y transferencias</div>
            </CardFooter>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={4}>
          <Card>
            <CardHeader color="danger" stats icon>
              <CardIcon color="danger"><Icon>arrow_upward</Icon></CardIcon>
              <p className={classes.cardCategory}>Total Egresos</p>
              <h3 className={classes.cardTitle}>${totales.egresos}</h3>
            </CardHeader>
            <CardFooter stats>
              <div className={classes.stats}>Salidas y movimientos contables</div>
            </CardFooter>
          </Card>
        </GridItem>
        <GridItem xs={12} sm={4}>
          <Card>
            <CardHeader color="info" stats icon>
              <CardIcon color="info"><Icon>account_balance_wallet</Icon></CardIcon>
              <p className={classes.cardCategory}>Saldo en Caja</p>
              <h3 className={classes.cardTitle}>${totales.saldo}</h3>
            </CardHeader>
            <CardFooter stats>
              <div className={classes.stats}>
                {infoCaja ? `Caja abierta desde: ${infoCaja.fechaApertura}` : "Sin caja abierta"}
              </div>
            </CardFooter>
          </Card>
        </GridItem>
      </GridContainer>

      <Card>
        <CardHeader color="primary">
          <h4 className={classes.cardTitle} style={{ color: "white" }}>Movimientos de Caja</h4>
        </CardHeader>
        <CardBody>
          <Button style={{ backgroundColor: "#00acc1", color: "white", marginBottom: 16 }} onClick={cargarMovimientos}>
            Actualizar
          </Button>
          <MaterialTable
            title=""
            columns={columns}
            data={movimientos}
            options={{
              search: true,
              paging: true,
              pageSize: 10,
              exportButton: true,
              headerStyle: { fontWeight: "bold" },
              rowStyle: rowData => ({
                backgroundColor: rowData.tipo === "E" ? "#f1f8e9" : "#fce4ec"
              })
            }}
          />
        </CardBody>
      </Card>
    </div>
  );
}
